import { SyncOutlined } from "@ant-design/icons";
import { Badge, Button, message, Modal, Collapse, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateFormat } from "~/components/date/DateFormat.jsx";
import { DateTimeFormat } from "~/components/date/DateTimeFormat.jsx";
import { $t } from "~/i18n.js";
import {
  saveFlightFromQueue,
  deleteFromQueue,
} from "~/redux/flights/flights.actions.js";

// rerun each seconds to check the queue to sync flights if possibles
const INTERVAL_TIMEOUT = 120 * 1000;

export const FlightQueueComponent = ({ syncData }) => {
  const dispatch = useDispatch();

  const { flight_queue, network_online, allFlights } = useSelector(
    (state) => state.flights
  );

  const [syncing, setSync] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  // setup dummy run
  // issue: setInterval does not have access to last state
  // as such we set an interval to increment a run, and set a useEffect on this run
  const [run, setRun] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setRun((run) => run + 1);
    }, INTERVAL_TIMEOUT);
    return () => {
      clearInterval(timer);
    };
  }, []);

  // setup useEffect to trigger on run
  // will trigger each time run is updated
  // each time it will have the last state loaded
  useEffect(() => {
    if (!navigator.onLine) return undefined;

    if (flight_queue && Object.keys(flight_queue).length > 0) {
      message.info(
        $t("Internet hersteld, syncronisatie ${l} vluchten gestart", {
          l: Object.keys(flight_queue).length,
        })
      );
      _fireSync();
    }
  }, [run, network_online]);

  const syncFlights = async () => {
    if (flight_queue && Object.keys(flight_queue).length === 0) {
      message.info($t("Geen vluchten"));
      return null;
    }

    if (!window.navigator.onLine) {
      message.error(
        $t("Internet offline, ${l} vluchten in geheugen", {
          l: flight_queue && Object.keys(flight_queue).length,
        })
      );
      return null;
    }

    message.info(
      $t("Syncroniseer ${l} vluchten", {
        l: flight_queue && Object.keys(flight_queue).length,
      })
    );
    _fireSync();
  };

  const getFlightQueueItems = () => {
    if (!flight_queue) return [];

    return Object.entries(flight_queue)
      .sort(
        ([, a], [, b]) => new Date(b._t).getTime() - new Date(a._t).getTime()
      )
      .map(([uuid, flight]) => {
        const content = Object.entries(flight)
          .filter(([key]) => key !== "uuid" && key !== "_t")
          .map(([key, value]) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <strong>{$t(key)}:</strong>{" "}
              {key === "start_methode" ? $t(value) : String(value)}
            </div>
          ));

        return {
          key: uuid,
          label: (
            <div>
              <span style={{ marginRight: 8 }}>{uuid}</span>
              <Tag color="blue">
                <DateTimeFormat
                  date={flight._t}
                  options={{
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }}
                />
              </Tag>
            </div>
          ),
          children: (
            <div>
              {content}
              <Button
                danger
                onClick={() => {
                  dispatch(deleteFromQueue(flight));
                  if (flights_badge <= 1) setModalVisible(false);
                }}
              >
                {$t("Verwijder")}
              </Button>
            </div>
          ),
        };
      });
  };

  const _fireSync = () => {
    setSync(true);
    // async save all flights
    Object.keys(flight_queue).forEach((uuid) => {
      // iterate over all flight events
      let queueElement = flight_queue[uuid];

      // _t is the time the flight was added to the queue
      const _t = queueElement._t;

      // if the flight was added to the queue more than 2 days ago, skip it
      if (
        !_t ||
        new Date().getTime() - new Date(_t).getTime() > 2 * 24 * 60 * 60 * 1000
      ) {
        dispatch(deleteFromQueue(queueElement));
      }

      // now check flight object and validate if the data inside the object is actually old
      const flight = allFlights.find((f) => f.uuid == uuid);
      console.log(JSON.stringify(flight));
      console.log(JSON.stringify(queueElement));

      if (flight) {
        // Check if any fields in queueElement are different from current flight object
        let fieldUpdateCount = 0;
        let updateElement = {};
        Object.keys(queueElement).forEach((field) => {
          // only include flight fields
          if (field !== "uuid" && field !== "_t") {
            // check if field is the same in the queueElement as the flight
            if (queueElement[field] === flight[field]) {
              fieldUpdateCount++;
              updateElement[field] = queueElement[field];
            }
          }
        });

        if (fieldUpdateCount > 0) {
          // Create updated queue element with current flight data for changed fields
          const updatedQueueElement = {
            ...updateElement,
            uuid: queueElement["uuid"],
            _t: queueElement["_t"],
          };

          // Update the queue with new data and save
          dispatch(saveFlightFromQueue(updatedQueueElement));
        } else {
          // All data in queue is old/unchanged, remove from queue
          dispatch(deleteFromQueue(queueElement));
        }
      } else {
        // Flight not found in allFlights, proceed with original queue element
        dispatch(saveFlightFromQueue(queueElement));
      }
    });
    setSync(false);
  };

  const flights_badge = flight_queue && Object.keys(flight_queue).length;

  return (
    <div className="row">
      

      {flights_badge > 0 && (
        <Button
          style={{ marginLeft: 8 }}
          size="small"
          loading={syncing}
          onClick={() => setModalVisible(true)}
          icon={<Badge
                style={{ marginLeft: 6 }}
                size="small"
                count={flights_badge}
              />}
        >
          <p className="small">{$t("wachtrij")}</p>
        </Button>
      )}

      <Button
        style={{ marginLeft: 8, marginRight: 8 }}
        size="small"
        loading={syncing}
        onClick={syncData}
        icon={<SyncOutlined style={{ color: "C4CED8" }} />}
      >
        <p className="small">sync</p>
      </Button>

      <Modal
        title={$t("Vlucht wachtrij")}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            {$t("Sluiten")}
          </Button>,
          <Button
            key="sync"
            type="primary"
            onClick={() => {
              syncFlights();
              setModalVisible(false);
            }}
          >
            {$t("Synchroniseren")}
          </Button>,
        ]}
      >
        <Collapse items={getFlightQueueItems()} size="small" />
      </Modal>
    </div>
  );
};
